/**
 * Modal de confirmación legal antes de comprar un pack.
 *
 * Muestra un checkbox obligatorio para que el usuario acepte expresamente
 * que el contenido es digital, de acceso inmediato, y que pierde el derecho
 * de desistimiento (art. 103.m TRLGDCU).
 */
import React, { useState, useEffect } from "react";
import {
	Box,
	Typography,
	Button,
	Checkbox,
	CircularProgress,
	Link,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import CardGiftcardRoundedIcon from "@mui/icons-material/CardGiftcardRounded";
import { BasicModal } from "./BasicModal/BasicModal";

const tokens = {
	primary: "#6366F1",
	textPrimary: "#0f172a",
	textSecondary: "#475569",
	textMuted: "#64748B",
	border: "rgba(0, 0, 0, 0.06)",
	radiusButton: "12px",
};

export default function PackConfirmModal({
	open,
	onClose,
	onConfirm,
	loading = false,
	packTitle = "Pack",
	packPrice = "",
}) {
	const [accepted, setAccepted] = useState(false);

	useEffect(() => {
		if (!open) setAccepted(false);
	}, [open]);

	return (
		<BasicModal width={420} showModal={open} handleClose={onClose}>
			<Box sx={{ pt: 0.5, pb: 1 }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1.25,
						mb: 2,
					}}
				>
					<Box
						sx={{
							width: 40,
							height: 40,
							borderRadius: "12px",
							bgcolor: "rgba(99, 102, 241, 0.12)",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							flexShrink: 0,
						}}
					>
						<CardGiftcardRoundedIcon
							sx={{ color: tokens.primary, fontSize: 22 }}
						/>
					</Box>
					<Box>
						<Typography
							sx={{
								fontWeight: 700,
								fontSize: "1.05rem",
								color: tokens.textPrimary,
								lineHeight: 1.3,
							}}
						>
							{packTitle}
						</Typography>
						{packPrice && (
							<Typography
								sx={{
									fontSize: "0.85rem",
									color: tokens.textMuted,
									mt: 0.25,
								}}
							>
								Pago único · {packPrice}
							</Typography>
						)}
					</Box>
				</Box>

				<Box
					sx={{
						mb: 2,
						p: 1.25,
						borderRadius: tokens.radiusButton,
						bgcolor: "rgba(0,0,0,0.03)",
						border: `1px solid ${tokens.border}`,
					}}
				>
					<Typography
						variant="caption"
						sx={{
							color: tokens.textMuted,
							lineHeight: 1.5,
							display: "block",
						}}
					>
						Este pack es un producto digital con acceso inmediato tras el pago.
					</Typography>
				</Box>

				<Box
					sx={{
						display: "flex",
						alignItems: "flex-start",
						gap: 0.75,
						mb: 2.5,
						cursor: "pointer",
						userSelect: "none",
					}}
					onClick={() => setAccepted((v) => !v)}
				>
					<Checkbox
						size="small"
						checked={accepted}
						onChange={(e) => setAccepted(e.target.checked)}
						onClick={(e) => e.stopPropagation()}
						sx={{
							color: tokens.textMuted,
							"&.Mui-checked": { color: tokens.primary },
							mt: 0.15,
							p: 0,
						}}
					/>
					<Typography
						variant="body2"
						sx={{
							color: tokens.textSecondary,
							lineHeight: 1.5,
							fontSize: "0.82rem",
						}}
					>
						Acepto iniciar el acceso inmediato al contenido digital y reconozco
						que pierdo mi derecho de desistimiento.
					</Typography>
				</Box>

				<Button
					variant="contained"
					fullWidth
					disabled={!accepted || loading}
					onClick={onConfirm}
					startIcon={
						loading ? (
							<CircularProgress size={18} color="inherit" />
						) : (
							<CardGiftcardRoundedIcon />
						)
					}
					sx={{
						textTransform: "none",
						fontWeight: 700,
						py: 1.25,
						borderRadius: tokens.radiusButton,
						background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
						boxShadow: "0 4px 14px rgba(99, 102, 241, 0.35)",
						"&:hover": {
							background: "linear-gradient(135deg, #5558E3 0%, #7C4DE8 100%)",
							boxShadow: "0 6px 20px rgba(99, 102, 241, 0.45)",
						},
						"&.Mui-disabled": {
							background: "rgba(0,0,0,0.08)",
							color: "rgba(0,0,0,0.3)",
							boxShadow: "none",
						},
					}}
				>
					Confirmar compra{packPrice ? ` — ${packPrice}` : ""}
				</Button>

				<Typography
					variant="caption"
					sx={{
						display: "block",
						mt: 1.5,
						textAlign: "center",
						color: tokens.textMuted,
						lineHeight: 1.5,
					}}
				>
					Al comprar aceptas nuestras{" "}
					<Link
						component={RouterLink}
						to="/legal/terminos"
						rel="nofollow"
						sx={{
							color: tokens.primary,
							fontWeight: 600,
							textDecoration: "none",
							"&:hover": { textDecoration: "underline" },
						}}
					>
						condiciones de contratación
					</Link>
					.
				</Typography>

				<Button
					variant="text"
					fullWidth
					disabled={loading}
					onClick={onClose}
					sx={{
						textTransform: "none",
						color: tokens.textMuted,
						fontSize: "0.85rem",
						mt: 0.75,
						"&:hover": {
							bgcolor: "rgba(0,0,0,0.03)",
							color: tokens.textPrimary,
						},
					}}
				>
					Cancelar
				</Button>
			</Box>
		</BasicModal>
	);
}
